import Dexie from 'dexie';

// Local-first storage so an active ride is never lost on refresh / offline.
export const localdb = new Dexie('sakaysafe');
// v1 schema (kept for migration continuity)
localdb.version(1).stores({
  pending_locations: '++id, ride_id, recorded_at',
  pending_events: '++id, ride_id',
  ride_cache: 'ride_id',
});
// v2 adds an outbox for rides created while offline (synced idempotently later).
localdb.version(2).stores({
  pending_locations: '++id, ride_id, recorded_at',
  pending_events: '++id, ride_id',
  ride_cache: 'ride_id',
  pending_rides: 'id, created_at',
});

// ---- ids ----
export function newClientId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0; const v = c === 'x' ? r : (r & 0x3) | 0x8; return v.toString(16);
  });
}

// Human-readable KR- id generated on-device for offline rides (server accepts it).
export function newLocalPublicRideId() {
  const d = new Date();
  const date = `${String(d.getFullYear()).slice(2)}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  const alpha = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let rand = '';
  for (let i = 0; i < 5; i++) rand += alpha[Math.floor(Math.random() * alpha.length)];
  return `KR-${date}-${rand}`;
}

// ---- location queue ----
export async function queueLocation(ride_id, point) {
  await localdb.pending_locations.add({ ride_id, ...point, recorded_at: new Date().toISOString() });
}

export async function queuedCount(ride_id) {
  return localdb.pending_locations.where('ride_id').equals(ride_id).count();
}

export async function flushLocations(ride_id, sender) {
  const items = await localdb.pending_locations.where('ride_id').equals(ride_id).toArray();
  for (const it of items) {
    try {
      await sender({ latitude: it.latitude, longitude: it.longitude, accuracy: it.accuracy, speed: it.speed, heading: it.heading });
      await localdb.pending_locations.delete(it.id);
    } catch (e) {
      break; // stop on first failure; retry later
    }
  }
}

// ---- ride cache (so an offline/locally-created ride still renders) ----
export async function cacheRide(ride) {
  if (ride && ride.id) await localdb.ride_cache.put({ ride_id: ride.id, ride, saved_at: Date.now() });
}

export async function getCachedRide(ride_id) {
  const row = await localdb.ride_cache.get(ride_id);
  return row ? row.ride : null;
}

// ---- offline ride outbox ----
export async function queueRide(payload) {
  await localdb.pending_rides.put({ ...payload, created_at: payload.client_started_at || new Date().toISOString() });
}

export async function getPendingRides() {
  return localdb.pending_rides.toArray();
}

export async function deletePendingRide(id) {
  await localdb.pending_rides.delete(id);
}

export async function hasPendingRide(id) {
  return !!(await localdb.pending_rides.get(id));
}

// Push any offline-created rides to the server (idempotent by id), then flush
// each ride's queued GPS points. `createSender(payload)` must POST /rides and
// `locSender(ride_id, point)` must POST a location.
export async function syncPendingRides(createSender, locSender) {
  const rides = await getPendingRides();
  for (const r of rides) {
    try {
      await createSender(r);            // idempotent create on the server
      await deletePendingRide(r.id);
      if (locSender) await flushLocations(r.id, (pt) => locSender(r.id, pt));
    } catch (e) {
      // leave it queued; will retry on next online event
      break;
    }
  }
}
